<script lang="ts" setup>
import { computed, ref } from 'vue';

import type { FormSchema, VFormRef } from '@modules/forms/components/VForm/types';
import type { MirrorsListCreateFormSchema } from '@modules/mirrors-list/submodules/create/submodules/manually/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useMirrorsListNamespace } from '@modules/mirrors-list/composables';
import {
  MIRRORS_LIST_JS_MAIN_ROUTE_NAME,
  MIRRORS_LIST_NO_JS_MAIN_ROUTE_NAME,
} from '@modules/mirrors-list/constants';
import {
  TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY,
} from '@modules/mirrors-list/submodules/create/submodules/manually/constants';
import { useMirrorAddManuallyMutation } from '@modules/mirrors-list/submodules/create/submodules/manually/mutations';

const { getTranslation } = useI18n();
const { isMirrorJsNamespace, mirrorNamespaceType } = useMirrorsListNamespace();
const form = ref<VFormRef>();

const {
  mutateAsync: addMirror,
  isLoading,
  errors,
  clearError,
} = useMirrorAddManuallyMutation();

const backRouteName = computed(() => (
  isMirrorJsNamespace.value ? MIRRORS_LIST_JS_MAIN_ROUTE_NAME : MIRRORS_LIST_NO_JS_MAIN_ROUTE_NAME
));

const domainLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'JS_MANUAL_LINK').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'NO_JS_MANUAL_LINK').value
));

const priorityLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'JS_MANUAL_PRIORITY').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'NO_JS_MANUAL_PRIORITY').value
));

const enabledLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'JS_MANUAL_ENABLED').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'NO_JS_MANUAL_ENABLED').value
));

const saveBtnLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'JS_SAVE').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'NO_JS_SAVE').value
));

const backBtnLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'JS_BACK').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_MANUALLY, 'NO_JS_BACK').value
));

const formSchema = computed<FormSchema<MirrorsListCreateFormSchema>>(() => ({
  domain: {
    type: EFormInputTypes.TEXT,
    label: domainLabel.value,
    required: true,
  },
  priority: {
    type: EFormInputTypes.NUMBER,
    label: priorityLabel.value,
    required: true,
  },
  enabled: {
    type: EFormInputTypes.CHECKBOX,
    label: enabledLabel.value,
  },
}));

async function onSubmit(data: MirrorsListCreateFormSchema): Promise<void> {
  await addMirror({ mirror_type: mirrorNamespaceType.value, ...data });
  form.value?.reset();
}
</script>

<template>
  <VForm v-data-qa-auto-id="'MirrorAddManuallyPage'"
    ref="form"
    :schema="formSchema"
    :submit-btn-label="saveBtnLabel"
    :is-loading="isLoading"
    :custom-errors="errors"
    @submit="onSubmit($event)"
    @interacted="clearError"
  >
    <template #secondaryBtn>
      <VBackButton
        :label="backBtnLabel"
        :to="{ name: backRouteName }"
      />
    </template>
  </VForm>
</template>
